import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function eur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function PublicFranchiseurTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/franchiseur-tracking/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center border border-stone-200">
          <AlertCircle size={32} className="mx-auto text-rose-500 mb-3"/>
          <div className="font-bold text-xl mb-2">Lien indisponible</div>
          <p className="text-sm text-stone-500">{error}</p>
          <p className="text-xs text-stone-400 mt-3">Contacte l'agence pour obtenir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center text-stone-500 text-sm">Chargement…</div>;

  const groups = { due: [], a_venir: [], payee: [], exclu: [] };
  data.rows.forEach((r) => (groups[r.status] || groups.a_venir).push(r));

  const groupCfg = [
    { key: "due", label: "Due — en attente de paiement", cls: "bg-amber-50 border-amber-300 text-amber-900", numCls: "text-amber-700" },
    { key: "a_venir", label: "À venir", cls: "bg-stone-50 border-stone-300 text-stone-900", numCls: "text-stone-700" },
    { key: "payee", label: "Payée", cls: "bg-emerald-50 border-emerald-300 text-emerald-900", numCls: "text-emerald-700" },
  ];

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4" data-testid="public-tracking-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-bold mb-2">Suivi rétrocommissions</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-stone-900" data-testid="public-fr-name">{data.franchiseur}</h1>
          <p className="text-sm text-stone-500 mt-2">Taux contractuel : <span className="font-bold text-stone-800">{data.taux_retrocommission}%</span> · Tous montants en HT</p>
          <p className="text-[11px] text-stone-400 mt-1">Vue en lecture seule fournie par Pourquoi Pas Design.</p>
        </div>

        {/* Totaux */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <KpiBlock label="Due" value={eur(data.totaux.due)} cls="bg-amber-50 border-amber-300 text-amber-900" numCls="text-amber-700"/>
          <KpiBlock label="À venir" value={eur(data.totaux.a_venir)} cls="bg-stone-50 border-stone-300 text-stone-900" numCls="text-stone-700"/>
          <KpiBlock label="Payée" value={eur(data.totaux.payee)} cls="bg-emerald-50 border-emerald-300 text-emerald-900" numCls="text-emerald-700"/>
        </div>

        {/* Groupes */}
        {data.rows.length === 0 ? (
          <div className="text-center text-stone-400 italic py-12">Aucune rétrocommission enregistrée pour le moment.</div>
        ) : (
          groupCfg.map((gc) => {
            const rows = groups[gc.key];
            if (!rows.length) return null;
            const total = rows.reduce((s, r) => s + r.retrocommission, 0);
            return (
              <div key={gc.key} className={`rounded-2xl border ${gc.cls} overflow-hidden mb-4`} data-testid={`public-group-${gc.key}`}>
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-bold uppercase tracking-widest text-xs">{gc.label}</span>
                    <span className="text-[10px] px-2 py-0.5 bg-white/70 rounded-full text-stone-600">{rows.length}</span>
                  </div>
                  <div className={`font-mono font-bold text-lg ${gc.numCls}`}>{eur(total)}</div>
                </div>
                <div className="overflow-x-auto bg-white border-t border-current/10">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left">Projet</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-right">Vendu HT</th>
                        <th className="px-3 py-2 text-right">Taux</th>
                        <th className="px-3 py-2 text-right">Rétro HT</th>
                        {gc.key === "payee" && <th className="px-3 py-2 text-left">Payée le</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.project_id} className="border-b border-stone-100 last:border-b-0">
                          <td className="px-3 py-2">
                            <div className="font-semibold">{r.nom}</div>
                            <div className="text-[10px] text-stone-500">{r.client}{r.ville ? ` · ${r.ville}` : ""}</div>
                          </td>
                          <td className="px-3 py-2 text-xs text-stone-600 capitalize">{(r.type_projet || "").replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-right font-mono">{eur(r.montant_vendu)}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.taux}%</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${gc.numCls}`}>{eur(r.retrocommission)}</td>
                          {gc.key === "payee" && <td className="px-3 py-2 text-xs text-emerald-700"><Check size={11} className="inline mr-1"/> {r.paid_at ? new Date(r.paid_at).toLocaleDateString("fr-FR") : "—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}

        <div className="text-center mt-10 text-[10px] text-stone-400 uppercase tracking-widest">
          Pourquoi Pas Design · Suivi confidentiel
        </div>
      </div>
    </div>
  );
}

function KpiBlock({ label, value, cls, numCls }) {
  return (
    <div className={`rounded-2xl border p-5 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className={`font-mono font-bold text-2xl mt-1 ${numCls}`}>{value}</div>
    </div>
  );
}
